// src/features/team/TeamCreateDialog.tsx
// Create a team — form-kit dialog (docs/ux/14-forms.md), opened from
// TeamListPage's "+ New" instead of routing to a /teams/new page.

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { useTeam } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea, SegmentedControl, FormRow } from '@/components/form';
import {
  teamTypeMap,
  teamVisibilityMap,
  validateTeamForm,
  mapTeamsToParentOptions,
  filterParentOptions,
  type ParentTeamOption,
} from './logic';

const TEAM_TYPE_OPTIONS = Object.keys(teamTypeMap).map((val) => ({ value: val, labelKey: teamTypeMap[val].labelKey }));
const VISIBILITY_OPTIONS = Object.keys(teamVisibilityMap).map((val) => ({ value: val, labelKey: teamVisibilityMap[val].labelKey }));

export interface TeamCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string | undefined) => void;
}

export function TeamCreateDialog({ open, onClose, onCreated }: TeamCreateDialogProps) {
  const { t } = useI18n();
  const store = useTeam();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [teamType, setTeamType] = useState('TEAM_TYPE_PROJECT');
  const [visibility, setVisibility] = useState('TEAM_VISIBILITY_PRIVATE');
  const [maxMembers, setMaxMembers] = useState('');
  const [parentId, setParentId] = useState('');
  const [parentQuery, setParentQuery] = useState('');
  const [parentOpen, setParentOpen] = useState(false);
  const [parentOptions, setParentOptions] = useState<ParentTeamOption[]>([]);
  const [moreFields, setMoreFields] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setTeamType('TEAM_TYPE_PROJECT');
    setVisibility('TEAM_VISIBILITY_PRIVATE');
    setMaxMembers('');
    setParentId('');
    setParentQuery('');
    setMoreFields(false);
    setError(null);
    store.ListTeams().then(() => setParentOptions(mapTeamsToParentOptions(store.items))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (parentRef.current && !parentRef.current.contains(e.target as Node)) setParentOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const dirty = !!(name.trim() || description.trim() || maxMembers || parentId);
  const filteredParents = filterParentOptions(parentOptions, parentQuery);
  const selectedParentName = parentOptions.find((o) => o.id === parentId)?.name ?? '';

  const submit = async () => {
    const errors = validateTeamForm({ name, max_members: maxMembers ? parseInt(maxMembers, 10) : 0 });
    if (Object.keys(errors).length > 0 || submitting) {
      if (errors.name) setError(t(errors.name, { field: t('team.field.name'), min: 2, max: 100 }));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await store.CreateTeam({
        team: {
          name: name.trim(),
          description: description.trim(),
          team_type: teamType,
          visibility,
          max_members: maxMembers ? parseInt(maxMembers, 10) : 0,
          ...(parentId ? { parent: { id: parentId } } : {}),
        },
      } as never);
      const newId = (res as any)?.team?.entity?.id || (res as any)?.entity?.id || (res as any)?.id;
      onCreated(newId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('team.create')}
      canSubmit={!!name.trim()}
      isSubmitting={submitting}
      dirty={dirty}
      aria-label={t('team.create')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('common.fewerFields') : t('common.moreFields')}
        </button>
      }
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p>}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={t('team.field.namePlaceholder')}
          initialFocus
          onEnter={() => descRef.current?.focus()}
          aria-label={t('team.field.name')}
        />
        <AutoGrowTextarea
          ref={descRef}
          value={description}
          onChange={setDescription}
          placeholder={t('team.field.descriptionPlaceholder')}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 px-3.5 pb-3">
        <SegmentedControl
          aria-label={t('team.field.type')}
          size="sm"
          value={teamType}
          onChange={setTeamType}
          options={TEAM_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
        />
        <SegmentedControl
          aria-label={t('team.field.visibility')}
          size="sm"
          value={visibility}
          onChange={setVisibility}
          options={VISIBILITY_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
        />
      </div>

      {moreFields && (
        <div className="space-y-3 border-t border-border px-3.5 py-3">
          <FormRow label={t('team.field.parent')} labelWidth="sm">
            <div className="relative max-w-xs" ref={parentRef}>
              <input
                type="text"
                value={parentOpen ? parentQuery : selectedParentName}
                onChange={(e) => { setParentQuery(e.target.value); setParentOpen(true); if (!e.target.value) setParentId(''); }}
                onFocus={() => { setParentOpen(true); setParentQuery(selectedParentName); }}
                placeholder={t('team.field.parentPlaceholder')}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {parentOpen && (
                <div className="absolute z-30 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-border bg-popover shadow-lg">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm italic text-muted-foreground hover:bg-accent"
                    onClick={() => { setParentId(''); setParentQuery(''); setParentOpen(false); }}
                  >
                    {t('team.field.parentNone')}
                  </button>
                  {filteredParents.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-accent"
                      onClick={() => { setParentId(opt.id); setParentQuery(opt.name); setParentOpen(false); }}
                    >
                      {opt.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </FormRow>
          <FormRow label={t('team.field.maxMembers')} labelWidth="sm" hint={t('team.field.maxMembersHint')}>
            <input
              type="text"
              inputMode="numeric"
              value={maxMembers}
              onChange={(e) => { if (e.target.value === '' || /^\d+$/.test(e.target.value)) setMaxMembers(e.target.value); }}
              placeholder={t('team.field.maxMembersUnlimited')}
              className="w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
        </div>
      )}
    </FormDialog>
  );
}

export default TeamCreateDialog;
